import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Music, Clock, CheckCircle, XCircle, Loader2, Search, Disc3, ExternalLink } from 'lucide-react';

const statusLabels = {
  pending: 'Afventer',
  approved: 'Godkendt',
  rejected: 'Afvist',
  queued: 'I kø',
  played: 'Spillet',
};

function formatDuration(ms) {
  if (!ms) return '';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

export default function MusicPage() {
  const [requests, setRequests] = useState([]);
  const [songTitle, setSongTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [spotifyQuery, setSpotifyQuery] = useState('');
  const [spotifyResults, setSpotifyResults] = useState([]);
  const [spotifySearching, setSpotifySearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState('');

  const loadRequests = () => {
    apiGet('/api/music-requests/me').then(r => setRequests(r.data.music_requests || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadRequests(); }, []);

  const duplicateRequest = useMemo(() => {
    if (selectedTrack?.id) {
      return requests.some(req => req.spotify_track_id === selectedTrack.id && req.status !== 'rejected');
    }
    const title = songTitle.trim().toLowerCase();
    const artistName = artist.trim().toLowerCase();
    if (!title) return false;
    return requests.some(req =>
      req.status !== 'rejected' &&
      (req.song_title || req.spotify_name || '').trim().toLowerCase() === title &&
      (!artistName || (req.artist || req.spotify_artist || '').trim().toLowerCase() === artistName)
    );
  }, [artist, requests, selectedTrack, songTitle]);

  const searchSpotify = async () => {
    if (!spotifyQuery.trim()) return;
    setSpotifySearching(true);
    setConfirmation('');
    try {
      const res = await apiGet(`/api/spotify/search?q=${encodeURIComponent(spotifyQuery)}`);
      setSpotifyResults(res.data.tracks || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke søge på Spotify');
    } finally {
      setSpotifySearching(false);
    }
  };

  const pickTrack = (track) => {
    setSelectedTrack(track);
    setSongTitle(track.name || '');
    setArtist(track.artist || '');
    setSpotifyResults([]);
    setSpotifyQuery(`${track.name} ${track.artist || ''}`.trim());
    setConfirmation('');
  };

  const clearTrack = () => {
    setSelectedTrack(null);
    setSongTitle('');
    setArtist('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!songTitle.trim() && !selectedTrack) return;
    if (duplicateRequest) {
      alert('Du har allerede ønsket denne sang.');
      return;
    }
    setSubmitting(true);
    setConfirmation('');
    try {
      await apiPost('/api/music-requests', {
        song_title: songTitle,
        artist,
        notes,
        spotify_track: selectedTrack,
      });
      setSongTitle('');
      setArtist('');
      setNotes('');
      setSelectedTrack(null);
      setSpotifyQuery('');
      setSpotifyResults([]);
      setConfirmation('Din sang er tilføjet til køen.');
      loadRequests();
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke sende musikønsket');
    } finally {
      setSubmitting(false);
    }
  };

  const statusIcons = {
    pending: <Clock className="w-3.5 h-3.5 text-amber-400" />,
    approved: <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />,
    rejected: <XCircle className="w-3.5 h-3.5 text-red-400" />,
    queued: <Loader2 className="w-3.5 h-3.5 text-blue-400" />,
    played: <Music className="w-3.5 h-3.5 text-purple-400" />,
  };

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-400',
    approved: 'bg-emerald-500/10 text-emerald-400',
    rejected: 'bg-red-500/10 text-red-400',
    queued: 'bg-blue-500/10 text-blue-400',
    played: 'bg-purple-500/10 text-purple-400',
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="music-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Musikønsker</h1>
      <p className="text-sm text-slate-400 mb-8">Søg på Spotify, vælg et nummer, eller skriv et ønske manuelt.</p>

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 sm:p-6 mb-6">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Find sang på Spotify</h3>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            type="text"
            value={spotifyQuery}
            onChange={(e) => setSpotifyQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchSpotify()}
            placeholder="Søg efter sang eller kunstner"
            className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-3 sm:py-2.5 outline-none placeholder:text-slate-600"
            data-testid="guest-spotify-search-input"
          />
          <Button type="button" onClick={searchSpotify} disabled={spotifySearching} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="guest-spotify-search-btn">
            {spotifySearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {spotifyResults.length > 0 && (
          <div className="space-y-2 max-h-80 overflow-y-auto mb-4" data-testid="guest-spotify-search-results">
            {spotifyResults.map(track => (
              <button key={track.id} type="button" onClick={() => pickTrack(track)} className="w-full flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors text-left">
                {track.album_art ? (
                  <img src={track.album_art} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Disc3 className="w-5 h-5 text-slate-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200 truncate">{track.name}</p>
                  <p className="text-xs text-slate-400 truncate">{track.artist}</p>
                  <p className="text-xs text-slate-600 truncate">{track.album} {formatDuration(track.duration_ms)}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {selectedTrack && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 mb-4 flex items-center gap-3" data-testid="selected-spotify-track">
            {selectedTrack.album_art && <img src={selectedTrack.album_art} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" />}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-100 truncate">{selectedTrack.name}</p>
              <p className="text-xs text-slate-400 truncate">{selectedTrack.artist}</p>
              <p className="text-xs text-slate-500 break-words">{selectedTrack.album} {formatDuration(selectedTrack.duration_ms)}</p>
            </div>
            <button type="button" onClick={clearTrack} className="min-h-9 rounded px-2 text-xs text-red-400 hover:text-red-300">Fjern</button>
          </div>
        )}

        {confirmation && (
          <div className="mb-4 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300" data-testid="music-confirmation">
            {confirmation}
          </div>
        )}

        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Nyt ønske</h3>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="music-request-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Sangtitel *</label>
              <input type="text" value={songTitle} onChange={(e) => { setSongTitle(e.target.value); setSelectedTrack(null); }}
                placeholder="Skriv sangens navn"
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-3 sm:py-2.5 outline-none placeholder:text-slate-600"
                data-testid="music-song-title" required={!selectedTrack} />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Kunstner</label>
              <input type="text" value={artist} onChange={(e) => { setArtist(e.target.value); setSelectedTrack(null); }}
                placeholder="Kunstnernavn"
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-3 sm:py-2.5 outline-none placeholder:text-slate-600"
                data-testid="music-artist" />
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1.5">Noter</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Fx spil den under middagen"
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-3 sm:py-2.5 outline-none placeholder:text-slate-600"
              data-testid="music-notes" />
          </div>
          {duplicateRequest && (
            <p className="text-sm text-amber-300">Du har allerede ønsket denne sang.</p>
          )}
          <Button type="submit" disabled={submitting || duplicateRequest}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 sm:py-2.5 rounded-md disabled:opacity-50"
            data-testid="music-submit-btn">
            {submitting ? 'Sender...' : 'Send ønske'}
          </Button>
        </form>
      </div>

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 sm:p-6">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Mine ønsker</h3>
        {loading ? (
          <p className="text-sm text-slate-400">Indlæser...</p>
        ) : requests.length === 0 ? (
          <p className="text-sm text-slate-500">Ingen ønsker endnu. Send dit første musikønske.</p>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => (
              <div key={req.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-slate-900/30 rounded-lg border border-slate-700/30" data-testid={`music-request-${req.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {req.spotify_album_art ? (
                    <img src={req.spotify_album_art} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                  ) : (
                    <Music className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-slate-200 truncate">{req.spotify_name || req.song_title}</p>
                    {(req.spotify_artist || req.artist) && <p className="text-xs text-slate-500 truncate">{req.spotify_artist || req.artist}</p>}
                    {req.spotify_external_url && (
                      <a href={req.spotify_external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300">
                        Spotify <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded flex-shrink-0 w-fit ${statusColors[req.status] || ''}`}>
                  {statusIcons[req.status]} {statusLabels[req.status] || req.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
